import { z } from "zod";

// ── Request Schemas ──────────────────────────────────────────────

export const configureSsoRequestSchema = z.object({
  protocol: z.enum(["SAML", "OIDC"]),
  displayName: z.string().min(1, "Display name is required"),
  metadataUrl: z.string().url("Must be a valid URL").optional(),
  emailAttribute: z.string().optional(),
  oidcIssuerUrl: z.string().url("Must be a valid URL").optional(),
  oidcClientId: z.string().optional(),
  oidcClientSecret: z.string().optional(),
});

export const updateSsoRequestSchema = z.object({
  status: z.enum(["ENABLED", "DISABLED", "TEST"]).optional(),
  displayName: z.string().min(1).optional(),
  metadataUrl: z.string().url("Must be a valid URL").optional(),
  emailAttribute: z.string().optional(),
  signingCert: z.string().optional(),
  encryptionCert: z.string().optional(),
});

// ── Response Schemas ─────────────────────────────────────────────

export const ssoConfigResponseSchema = z.object({
  status: z.enum(["ENABLED", "DISABLED", "TEST"]),
  protocol: z.enum(["SAML", "OIDC"]),
  idpName: z.string().optional().nullable(),
  displayName: z.string().optional().nullable(),
  callbackUrl: z.string().optional().nullable(),
  metadataUrl: z.string().optional().nullable(),
  emailAttribute: z.string().optional().nullable(),
  signingCert: z.string().optional().nullable(),
  encryptionCert: z.string().optional().nullable(),
  oidcIssuerUrl: z.string().optional().nullable(),
  oidcClientId: z.string().optional().nullable(),
});

export const spMetadataResponseSchema = z.object({
  saml: z
    .object({
      entityId: z.string(),
      acsUrl: z.string(),
      sloUrl: z.string().optional().nullable(),
      defaultEmailAttribute: z.string().optional().nullable(),
      nameIdFormat: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  oidc: z
    .object({
      redirectUri: z.string(),
    })
    .optional()
    .nullable(),
});

export const ssoPreCheckResponseSchema = z.object({
  activeUsers: z.number(),
  warning: z.string().optional().nullable(),
});

export const configureSsoResponseSchema = z.object({
  status: z.string(),
  warning: z.string().optional().nullable(),
});

export const messageResponseSchema = z.object({
  message: z.string(),
});

// ── Inferred Types ───────────────────────────────────────────────

export type ConfigureSsoRequest = z.infer<typeof configureSsoRequestSchema>;
export type UpdateSsoRequest = z.infer<typeof updateSsoRequestSchema>;
export type SsoConfigResponse = z.infer<typeof ssoConfigResponseSchema>;
export type SpMetadataResponse = z.infer<typeof spMetadataResponseSchema>;
export type SsoPreCheckResponse = z.infer<typeof ssoPreCheckResponseSchema>;
export type ConfigureSsoResponse = z.infer<typeof configureSsoResponseSchema>;
export type SsoMessageResponse = z.infer<typeof messageResponseSchema>;

// ── Form Types (internal) ────────────────────────────────────────

export interface SsoFormValues {
  displayName: string;
  metadataUrl: string;
}
